import { useEffect, useRef } from 'react';
import { differenceInSeconds } from '@repo/date-time';
import { useFetchReservations, useInvalidateReservations, useReservationsLive } from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { useLocation, useNavigate } from 'react-router-dom';

import PaymentBlikWaitContents from '@features/reservation/components/payment/PaymentBlikWaitContents';
import PaymentExtensionContents from '@features/reservation/components/payment/PaymentExtensionContents';
import PaymentFutureSpanContents from '@features/reservation/components/payment/PaymentFutureSpanContents';
import PaymentMethodsContents from '@features/reservation/components/payment/PaymentMethodsContents';
import PaymentPendingContents from '@features/reservation/components/payment/PaymentPendingContents';
import { readExtensionPayment } from '@features/reservation/utils/readExtensionPayment';
import { readFutureSpanPayment } from '@features/reservation/utils/readFutureSpanPayment';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

export default function ReservationPaymentFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const testId = TestId.root('reservation.paymentFlow');
  // Paying for an extension of an active reservation or for a future span of a
  // revolving one, not for the pending one: the pending-reservation effects below stand down.
  const extensionPayment = readExtensionPayment(location.state);
  const futureSpanPayment = readFutureSpanPayment(location.state);
  const isSpanPayment = extensionPayment !== undefined || futureSpanPayment !== undefined;
  const fetchReservations = useFetchReservations();
  const invalidateReservations = useInvalidateReservations();
  const { reservations, loadingReservations } = useReservationsLive(
    { only: 'pending' },
    { withParking: true, withVehicle: true, withSpot: true, withOrder: true }
  );
  const pendingReservation = reservations[0];

  const payment = pendingReservation?.order?.payment;
  const paymentMethod = payment?.method;

  const trackedReservationIdRef = useRef<string>('');

  // Remember the reservation we're paying so we can still read it by id after it
  // leaves the pending list (a successful payment moves it to `confirmed`).
  useEffect(() => {
    if (pendingReservation) {
      trackedReservationIdRef.current = pendingReservation.id;
    }
  }, [pendingReservation]);

  // A redirect-based payment that hasn't completed yet: hand off to the gateway.
  useEffect(() => {
    if (isSpanPayment) return;
    if (payment?.status === 'pending' && paymentMethod === 'redirect' && payment.redirectUrl) {
      window.location.assign(payment.redirectUrl);
    }
  }, [isSpanPayment, payment?.status, paymentMethod, payment?.redirectUrl]);

  // A failed payment fails the flow immediately, even if the reservation is
  // still pending (BLIK FAILED leaves it pending for a retry). Success is not
  // handled here — it's settled by the by-id check once the reservation leaves
  // the pending list.
  useEffect(() => {
    if (isSpanPayment) return;
    if (payment?.status === 'failed') {
      navigate(ROUTE_PATHS[RouteName.RESERVATION_FAILURE], { replace: true });
    }
  }, [isSpanPayment, payment?.status, navigate]);

  // The reservation left the pending list, or there was never one to pay. This
  // fires once on the pending → gone transition (then we navigate away).
  useEffect(() => {
    if (isSpanPayment || loadingReservations || pendingReservation) return;

    // Nobody is mid-payment here — send the visitor to their reservations.
    if (!trackedReservationIdRef.current) {
      toast.danger('Brak rezerwacji w trakcie płatności.', testId.append('noReservationToast'));
      navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS], { replace: true });
      return;
    }

    // It disappeared while we were paying — settle by re-reading it by id.
    void (async () => {
      const [reservation] = await fetchReservations({ id: trackedReservationIdRef.current }, { withOrder: true });
      const isPaid = reservation?.order?.payment?.status === 'succeeded';
      // The free parking time pays for the whole reservation: it is confirmed with no order to pay.
      const isConfirmedFree =
        !!reservation && !reservation.order && (reservation.status === 'confirmed' || reservation.status === 'active');
      navigate(ROUTE_PATHS[isPaid || isConfirmedFree ? RouteName.RESERVATION_SUCCESS : RouteName.RESERVATION_FAILURE], {
        replace: true,
      });
    })();
    // Runs only on the loading/pending transition; fetchReservations and testId
    // are recreated each render and must not retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpanPayment, loadingReservations, pendingReservation, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleExpire = () => {
    invalidateReservations();
  };

  if (extensionPayment) return <PaymentExtensionContents extensionPayment={extensionPayment} />;

  if (futureSpanPayment) return <PaymentFutureSpanContents futureSpanPayment={futureSpanPayment} />;

  if (loadingReservations) return <PleaseWaitAnimation testId={testId} />;

  if (!pendingReservation) return null;

  const secondsToDeadline = pendingReservation.expiresAt
    ? Math.max(0, differenceInSeconds(pendingReservation.expiresAt, new Date()))
    : undefined;

  if (!payment) {
    return (
      <PaymentMethodsContents
        reservation={pendingReservation}
        secondsToDeadline={secondsToDeadline}
        onExpire={handleExpire}
      />
    );
  }

  if (paymentMethod === 'blik' || paymentMethod === 'blikWithoutCode') {
    return <PaymentBlikWaitContents onGoBack={handleBack} />;
  }

  return <PaymentPendingContents onGoBack={handleBack} />;
}
